"use client";

import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";

const MAX_REASON = 1000;

export type TierRequestSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tierName: string;
  hiiveName: string;
  /** Named so the sheet can say what this request actually unlocks. */
  eventName?: string | null;
  /** Verified address the decision will be mailed to, if we have one. */
  contactEmail?: string | null;
  isSubmitting: boolean;
  onSubmit: (reason: string) => void;
};

/**
 * The confirm step in front of a join request.
 *
 * Before this, tapping a tier fired the request immediately and the "Message to
 * the organiser" box only existed on the *receipt* screen, in the edit drawer —
 * so the one moment a person actually has something to say
 * ("I DJ'd at your last one", "I'm a friend of Ravi's") was the one moment we
 * did not ask. Anyone who wanted to say it had to send an empty request first
 * and then discover they could edit it.
 *
 * The message stays optional and the primary button never changes label,
 * because the request is complete without it. The point is to offer the box,
 * not to imply an empty request is a worse one.
 */
export function TierRequestSheet({
  open,
  onOpenChange,
  tierName,
  hiiveName,
  eventName,
  contactEmail,
  isSubmitting,
  onSubmit,
}: TierRequestSheetProps) {
  const [reason, setReason] = useState("");

  // A closed sheet is a discarded draft. Keeping the text would resurface
  // somebody's half-written note against a different tier they later tapped.
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent className="mx-auto w-full max-w-md rounded-t-[28px] border-border bg-surface text-text-primary">
        <DrawerHeader className="px-5 pb-2 text-left">
          <DrawerTitle className="text-xl font-semibold tracking-tight">
            Request to join {tierName}
          </DrawerTitle>
          <DrawerDescription className="mt-1 text-sm">
            {eventName
              ? `${hiiveName} reviews every request. Once you're in, ${eventName} is yours to book.`
              : `${hiiveName} reviews every request before letting someone in.`}
          </DrawerDescription>
        </DrawerHeader>

        <div className="space-y-4 px-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label
                htmlFor="tier-request-reason"
                className="cursor-pointer text-sm font-medium text-text-secondary"
              >
                Message to the organiser{" "}
                <span className="text-text-tertiary">(optional)</span>
              </label>
              <span className="text-[11px] text-text-tertiary tabular-nums">
                {reason.length} / {MAX_REASON}
              </span>
            </div>
            <Textarea
              id="tier-request-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Anything they should know about you? Totally optional."
              maxLength={MAX_REASON}
              className="min-h-[104px] rounded-xl border-border bg-surface-alt text-sm focus:border-brand focus:ring-1 focus:ring-brand/20"
            />
          </div>

          <p className="text-xs leading-relaxed text-text-tertiary">
            {contactEmail
              ? `We'll email ${contactEmail} the moment they decide.`
              : "You'll get a notification in the app the moment they decide."}
          </p>
        </div>

        <DrawerFooter className="flex flex-row gap-3 px-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="h-11 flex-1 rounded-xl text-sm"
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={() => onSubmit(reason.trim())}
            disabled={isSubmitting}
            /*
              `dark:bg-brand` is load-bearing. The Button's default variant
              carries `dark:bg-white/[0.08]`, and a plain `bg-brand` never
              beats a `dark:` modifier — so the primary action on this sheet
              was rendering as a muted grey button next to an outline one,
              with nothing to say which was which.
            */
            className="h-11 flex-1 rounded-xl bg-brand text-sm font-semibold text-brand-foreground hover:bg-brand/90 dark:bg-brand dark:hover:bg-brand/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-1.5 size-4 animate-spin" />
                Sending
              </>
            ) : (
              "Send request"
            )}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
